import React, { useState, useRef, useContext } from 'react';
import { Bot, Zap, Camera, Mic, MapPin, Monitor } from 'lucide-react';
import { useWorkspace } from '../context/WorkspaceContext.js';
import { ChatContext } from '../context/ChatContext.js';
import './RapidMindAgentButton.css';

const RapidMindAgentButton = () => {
  const { workspaceData } = useWorkspace();
  const { addChat } = useContext(ChatContext) || {};
  const [isActivated, setIsActivated] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [currentTask, setCurrentTask] = useState('');
  const [taskResult, setTaskResult] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [showCompletedTaskModal, setShowCompletedTaskModal] = useState(false);
  const [completedTaskData, setCompletedTaskData] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const generateRapidMindPlan = async () => {
    if (!workspaceData.currentTask || workspaceData.currentTask.trim() === '') {
      alert('Please specify a task first in your workspace.');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch('/api/ai/rapidmind', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          task: workspaceData.currentTask,
          expertise: workspaceData.selectedExpertise,
          userRole: workspaceData.userType || 'freelancer',
          recommendedTools: workspaceData.recommendedTools
        }),
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setCompletedTaskData(data);
        setShowCompletedTaskModal(true);

        const planMessage = {
          type: 'ai',
          content: `🤖 **RapidMind AI Agent Completed Task for ${workspaceData.userType || 'Freelancer'}:**\n\n${data.completedTask}`,
          timestamp: new Date()
        };
        if (addChat) addChat(planMessage.content, planMessage.type);
      } else {
        const errorMessage = {
          type: 'ai',
          content: '❌ Failed to generate plan. Please try again.',
          timestamp: new Date()
        };
        if (addChat) addChat(errorMessage.content, errorMessage.type);
      }
    } catch (error) {
      console.error('RapidMind plan generation error:', error);
      const errorMessage = {
        type: 'ai',
        content: '❌ Error generating plan. Please check your connection.',
        timestamp: new Date()
      };
      if (addChat) addChat(errorMessage.content, errorMessage.type);
    } finally {
      setIsGenerating(false);
    }
  };

  // Auto-execute when tools are recommended
  React.useEffect(() => {
    if (workspaceData.recommendedTools && workspaceData.recommendedTools.length > 0 && workspaceData.currentTask && !isActivated && !isGenerating) {
      // Auto-activate and start task completion
      setIsActivated(true);
      generateRapidMindPlan();
    }
  }, [workspaceData.recommendedTools, workspaceData.currentTask, isActivated, isGenerating]);

  const handleClick = async () => {
    if (!isActivated) {
      // Request multiple permissions for device access
      const permissions = [];

      try {
        // Request notification permission
        if ('Notification' in window) {
          const notificationPermission = await Notification.requestPermission();
          permissions.push({ type: 'notifications', granted: notificationPermission === 'granted' });
        }

        // Request camera permission
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          try {
            const cameraStream = await navigator.mediaDevices.getUserMedia({ video: true });
            cameraStream.getTracks().forEach(track => track.stop()); // Stop immediately
            permissions.push({ type: 'camera', granted: true });
          } catch (error) {
            permissions.push({ type: 'camera', granted: false });
          }
        }

        // Request microphone permission
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          try {
            const micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            micStream.getTracks().forEach(track => track.stop()); // Stop immediately
            permissions.push({ type: 'microphone', granted: true });
          } catch (error) {
            permissions.push({ type: 'microphone', granted: false });
          }
        }

        // Request geolocation permission
        if ('geolocation' in navigator) {
          try {
            const position = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 });
            });
            permissions.push({ type: 'geolocation', granted: true });
          } catch (error) {
            permissions.push({ type: 'geolocation', granted: false });
          }
        }

        // Check file system access (limited in browsers)
        permissions.push({ type: 'filesystem', granted: 'showDirectoryPicker' in window });

        // Check if any permissions were granted
        const grantedPermissions = permissions.filter(p => p.granted);

        if (grantedPermissions.length > 0) {
          setIsActivated(true);
          alert(`RapidMind AI Agent activated! Granted access to: ${grantedPermissions.map(p => p.type).join(', ')}. It will now help you with your tasks.`);
        } else {
          alert('RapidMind AI Agent activated with limited permissions. Some features may be restricted.');
          setIsActivated(true);
        }

        // Here you can integrate with the AI agent functionality
        console.log('Permissions granted:', permissions);

      } catch (error) {
        console.error('Permission request error:', error);
        alert('Some permissions could not be requested. AI Agent activated with available permissions.');
        setIsActivated(true);
      }
    } else {
      // Generate AI plan based on workspace data
      await generateRapidMindPlan();
    }
  };

  // Task automation functions
  const takePhoto = async () => {
    setCurrentTask('Taking photo...');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      videoRef.current.srcObject = stream;

      return new Promise((resolve) => {
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setTimeout(() => {
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            context.drawImage(videoRef.current, 0, 0);

            canvas.toBlob((blob) => {
              const url = URL.createObjectURL(blob);
              setTaskResult(`Photo captured! Size: ${blob.size} bytes`);
              stream.getTracks().forEach(track => track.stop());
              resolve(url);
            });
          }, 1000);
        };
      });
    } catch (error) {
      setTaskResult('Failed to take photo: ' + error.message);
    }
    setCurrentTask('');
  };

  const recordAudio = async () => {
    setCurrentTask('Recording audio...');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      const chunks = [];

      mediaRecorder.ondataavailable = (event) => {
        chunks.push(event.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);
        setTaskResult(`Audio recorded! Duration: ${chunks.length} chunks`);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setTimeout(() => {
        mediaRecorder.stop();
      }, 3000); // Record for 3 seconds

    } catch (error) {
      setTaskResult('Failed to record audio: ' + error.message);
    }
    setCurrentTask('');
  };

  const getLocation = async () => {
    setCurrentTask('Getting location...');
    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 10000 });
      });

      const { latitude, longitude } = position.coords;
      setTaskResult(`Location: ${latitude.toFixed(6)}, ${longitude.toFixed(6)}`);
    } catch (error) {
      setTaskResult('Failed to get location: ' + error.message);
    }
    setCurrentTask('');
  };

  const captureScreen = async () => {
    setCurrentTask('Capturing screen...');
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({ video: true });
      const video = document.createElement('video');
      video.srcObject = stream;

      return new Promise((resolve) => {
        video.onloadedmetadata = () => {
          video.play();
          setTimeout(() => {
            const canvas = canvasRef.current;
            const context = canvas.getContext('2d');
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            context.drawImage(video, 0, 0);

            canvas.toBlob((blob) => {
              const url = URL.createObjectURL(blob);
              setTaskResult(`Screen captured! Size: ${blob.size} bytes`);
              stream.getTracks().forEach(track => track.stop());
              resolve(url);
            });
          }, 1000);
        };
      });
    } catch (error) {
      setTaskResult('Failed to capture screen: ' + error.message);
    }
    setCurrentTask('');
  };

  const handleTask = async (taskType) => {
    switch (taskType) {
      case 'photo':
        await takePhoto();
        break;
      case 'audio':
        await recordAudio();
        break;
      case 'location':
        await getLocation();
        break;
      case 'screen':
        await captureScreen();
        break;
      default:
        setTaskResult('Unknown task type');
    }
  };

  return (
    <>
      <button
        className={`rapidmind-agent-button ${isActivated ? 'activated' : ''} ${isGenerating ? 'generating' : ''}`}
        onClick={handleClick}
        disabled={isGenerating}
        title={isActivated ? "Generate AI Plan" : "Activate RapidMind AI Agent"}
      >
        <Bot size={20} />
        <Zap size={16} className="zap-icon" />
        <span>{isGenerating ? 'Generating...' : 'RapidMind AI'}</span>
      </button>

      {/* Hidden video and canvas for media capture */}
      <video ref={videoRef} style={{ display: 'none' }} />
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {showPermissionModal && (
        <div className="permission-modal">
          <div className="permission-content">
            <h3>Enable RapidMind AI Agent</h3>
            <p>Allow notifications to receive task assistance and updates from your AI agent.</p>
            <div className="permission-buttons">
              <button onClick={() => setShowPermissionModal(false)}>Cancel</button>
              <button onClick={handleClick}>Allow</button>
            </div>
          </div>
        </div>
      )}

      {showTaskModal && (
        <div className="task-modal">
          <div className="task-content">
            <h3>RapidMind AI Agent Tasks</h3>
            <p>Choose a task to automate:</p>

            <div className="task-buttons">
              <button onClick={() => handleTask('photo')} className="task-btn">
                <Camera size={20} />
                Take Photo
              </button>

              <button onClick={() => handleTask('audio')} className="task-btn">
                <Mic size={20} />
                Record Audio
              </button>

              <button onClick={() => handleTask('location')} className="task-btn">
                <MapPin size={20} />
                Get Location
              </button>

              <button onClick={() => handleTask('screen')} className="task-btn">
                <Monitor size={20} />
                Capture Screen
              </button>
            </div>

            {currentTask && (
              <div className="task-status">
                <p>{currentTask}</p>
              </div>
            )}

            {taskResult && (
              <div className="task-result">
                <p><strong>Result:</strong> {taskResult}</p>
              </div>
            )}

            <div className="modal-buttons">
              <button onClick={() => setShowTaskModal(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showCompletedTaskModal && completedTaskData && (
        <div className="completed-task-modal">
          <div className="completed-task-content">
            <h3>🎉 Task Completed Successfully!</h3>
            <div className="task-summary">
              <div className="summary-item">
                <strong>Task Type:</strong> {completedTaskData.taskType || 'Professional Task'}
              </div>
              <div className="summary-item">
                <strong>Expertise Used:</strong> {completedTaskData.expertise?.join(', ') || 'AI Agent'}
              </div>
              <div className="summary-item">
                <strong>Tools Applied:</strong> {completedTaskData.tools?.join(', ') || 'Advanced AI Tools'}
              </div>
              <div className="summary-item">
                <strong>Completion Time:</strong> {completedTaskData.completionTime || 'Instant'}
              </div>
              <div className="summary-item">
                <strong>Status:</strong> {completedTaskData.status || '✅ Completed'}
              </div>
            </div>

            <div className="task-deliverables">
              <h4>📋 Your Professional Task Deliverables:</h4>
              <div className="deliverables-content">
                <pre>{completedTaskData.completedTask}</pre>
              </div>
            </div>

            <div className="task-actions">
              <button
                className="download-btn"
                onClick={() => {
                  const blob = new Blob([completedTaskData.completedTask], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `rapidmind-task-${Date.now()}.txt`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  URL.revokeObjectURL(url);
                }}
              >
                📥 Download Results
              </button>
              <button
                className="share-btn"
                onClick={() => {
                  navigator.clipboard.writeText(completedTaskData.completedTask);
                  alert('Task results copied to clipboard!');
                }}
              >
                📋 Copy to Clipboard
              </button>
              <button
                className="close-btn"
                onClick={() => setShowCompletedTaskModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RapidMindAgentButton;
